import {useLeaflet} from "react-leaflet";
import React, {useContext, useEffect, useState} from "react";
import L from "leaflet";
import 'leaflet-draw';
import FilterContext from "../FilterContext";

export const DrawControl = () => {
    const l = useLeaflet();

    const [drawnRegions] = useState(new L.FeatureGroup().addTo(l.map));
    const [context, setContext] = useContext(FilterContext);

    //Circle was drawn on the map
    const queryCircle = (layer) => {
        let center = layer.getLatLng();
        let radius = layer.getRadius();

        setContext({
            ...context,
            filters: {
                ...context.filters,
                "region": {
                    "@type": "circle",
                    "lat": center["lat"],
                    "lon": center["lng"],
                    "radius": radius,
                }
            },
            searchLocation: undefined
        });
        drawnRegions.clearLayers(); //Clear all previously drawn shapes
        drawnRegions.addLayer(layer); //Add the newly drawn shape to the map
        l.map.fitBounds(layer.getBounds()); //Fit the map to the newly drawn shape
    }

    //Polygon (either rectangle or hand-drawn) was drawn on the map
    const queryPolygon = (layer) => {
        const points = layer.getLatLngs()[0]; //Get all points in the boundary of the polygon
        const coords = points.map(it => [it.lng, it.lat]); //Create list of coordinates from the points

        setContext({
            ...context,
            filters: {
                ...context.filters,
                "region": {
                    "@type": "polygon",
                    "points": coords,
                    "numPoints": coords.length
                }
            },
            searchLocation: undefined
        });
        drawnRegions.clearLayers(); //Clear all previously drawn shapes
        drawnRegions.addLayer(layer); //Add the newly drawn shape to the map
        l.map.fitBounds(layer.getBounds()); //Fit the map to the newly drawn shape
    }

    useEffect(() => {
        if (!context.filters.region) { //If no region filters exist, clear all drawn regions on the map
            drawnRegions.clearLayers();
        }
    }, [context, drawnRegions]);  //added dependencies

    // Address-search region is rendered by SearchLocationLayer, so any user-drawn shape
    // must be cleared to avoid two competing region visualizations.
    useEffect(() => {
        if (context.searchLocation) {
            drawnRegions.clearLayers();
        }
    }, [context.searchLocation, drawnRegions]);


    useEffect(() => {
        const leafletMap = l.map;

        // overwrite the delete tool's enable method to simply delete all layers instead of opening the delete menu
        L.EditToolbar.Delete.include({
          enable: function () {
            setContext(prev => ({
                ...prev,
                filters: {
                    ...prev.filters,
                    region: undefined
                },
                bounds: undefined,
                searchLocation: undefined
            }));
          }
        })

        L.drawLocal.edit.toolbar.buttons.edit = 'Edit shapes';
        L.drawLocal.edit.toolbar.buttons.editDisabled = 'No shapes to edit';
        L.drawLocal.edit.toolbar.buttons.remove = 'Delete shapes';
        L.drawLocal.edit.toolbar.buttons.removeDisabled = 'No shapes to delete';

        //Create the draw controls on the map
        leafletMap.addControl(new L.Control.Draw({
            position: "topright",
            draw: {
                polyline: false,
                circlemarker: false,
                marker: false
            },
            edit: {
                featureGroup: drawnRegions
            }
        }));
    }, [drawnRegions, l.map])  //added dependencies

    useEffect(() => {
        const leafletMap = l.map;

        //When a drawn shape is edited, grab the layer type and query based on shape that was edited
        leafletMap.on(L.Draw.Event.EDITED, (e) => {
            let layers = e.layers;
            layers.eachLayer((layer) => {
                if (layer instanceof L.Circle) {
                    queryCircle(layer);
                } else {
                    queryPolygon(layer);
                }
            });
        });

        leafletMap.off(L.Draw.Event.DELETED);

        //When a shape is deleted, remove the region filter
        leafletMap.on(L.Draw.Event.DELETED, e => {
            setContext({
                ...context,
                filters: {
                    ...context.filters,
                    region: undefined
                },
                bounds: undefined,
                searchLocation: undefined
            });
        });

        leafletMap.off(L.Draw.Event.CREATED);

        //When a shape is drawn, grab the layer type and query based on shape that was created
        leafletMap.on(L.Draw.Event.CREATED, e => {
            const type = e.layerType;
            const layer = e.layer;
            layer.setStyle({
                        fillColor: '#7996AB', // Shape Fill color
                        color: '#7996AB',   // Shape Outline color
                    });
            if (type === 'circle') {
                queryCircle(layer);
            } else if (type === 'rectangle' || type === 'polygon') {
                queryPolygon(layer);
            }
        });
    }, [context, l.map, queryCircle, queryPolygon, setContext]) //added dependencies

    return <></>
}
